import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Banner, Button, ConfirmDialog, Spinner } from "@/components/ui";
import { ReopenFields, defaultValidUntil, inputToIso } from "@/components/BlockControls";
import { approveReopenRequest, errorDetail, listReopenRequests, rejectReopenRequest } from "@/lib/api";
import type { ReopenRequest } from "@/lib/types";
import { formatDateTime } from "@/lib/format";
import { useLanguage } from "@/i18n";

/**
 * The office queue for reopening submitted blocks. A surveyor asks from the
 * field app with a reason; here staff approve it (setting how long the block
 * stays open -- required) or reject it (saying why -- required). The approver
 * is whoever is signed in. Below the queue, every past request and how it
 * ended: rejected, completed (resubmitted and locked again) or expired.
 */
export default function ReopenRequestsPage() {
  const { t, lang } = useLanguage();
  const p = t.reopenRequestsPage;
  const c = t.blockControls;
  const [rows, setRows] = useState<ReopenRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState<ReopenRequest | null>(null);
  const [rejecting, setRejecting] = useState<ReopenRequest | null>(null);
  const [validUntil, setValidUntil] = useState(defaultValidUntil());
  const [note, setNote] = useState("");

  const when = (iso: string | null | undefined) =>
    iso ? formatDateTime(iso, lang, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await listReopenRequests());
    } catch (err) {
      setError(errorDetail(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<unknown>, notice: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      toast.success(notice);
      setApproving(null);
      setRejecting(null);
      await load();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusy(false);
    }
  }

  const pending = rows?.filter((r) => r.status === "pending") ?? [];
  const decided = rows?.filter((r) => r.status !== "pending") ?? [];
  const place = (r: ReopenRequest) =>
    [r.zone_name, r.ward_name].filter(Boolean).join(" / ");
  const who = (r: ReopenRequest) =>
    r.requested_by_name ? `${r.requested_by_name} (${r.requested_by})` : r.requested_by ?? "—";
  const validIso = inputToIso(validUntil);

  const th = "px-2.5 py-1.5 font-semibold";
  const td = "px-2.5 py-1.5 align-top";

  return (
    <AppShell crumbs={[{ label: p.crumb }]}>
      <div className="mx-auto max-w-[1300px] space-y-5 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-ink">{p.heading}</h1>
            <p className="mt-0.5 text-xs text-ink-muted">{p.subheading}</p>
          </div>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void load()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            {p.refresh}
          </Button>
        </div>

        {error && approving === null && rejecting === null && <Banner tone="danger">{error}</Banner>}

        <section className="rounded-md border border-line bg-surface p-3">
          <h2 className="mb-2 text-sm font-semibold text-ink">
            {p.pendingHeading}
            {pending.length > 0 && (
              <span className="ml-2 rounded bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-ink-secondary">
                {pending.length}
              </span>
            )}
          </h2>
          {rows === null ? (
            <Spinner className="h-4 w-4" />
          ) : pending.length === 0 ? (
            <p className="py-2 text-xs text-ink-muted">{p.pendingNone}</p>
          ) : (
            <div className="thin-scroll overflow-x-auto rounded border border-line">
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className={th}>{p.colBlock}</th>
                    <th className={th}>{p.colRequestedBy}</th>
                    <th className={th}>{p.colReason}</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {pending.map((r) => (
                    <tr key={r.id}>
                      <td className={`${td} text-ink`}>
                        <span className="font-medium">{r.block_name ?? `#${r.block_id}`}</span>
                        <p className="text-2xs text-ink-muted">{place(r)}</p>
                      </td>
                      <td className={`${td} text-ink-secondary`}>
                        {who(r)}
                        <p className="text-2xs tabular-nums text-ink-muted">{when(r.requested_at)}</p>
                      </td>
                      <td className={`${td} max-w-[420px] whitespace-pre-wrap text-ink-secondary`}>{r.reason}</td>
                      <td className={`${td} whitespace-nowrap text-right`}>
                        <div className="flex justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={busy}
                            onClick={() => {
                              setError(null);
                              setValidUntil(defaultValidUntil());
                              setNote("");
                              setApproving(r);
                            }}
                          >
                            {p.approve}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() => {
                              setError(null);
                              setNote("");
                              setRejecting(r);
                            }}
                          >
                            {p.reject}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-md border border-line bg-surface p-3">
          <h2 className="mb-2 text-sm font-semibold text-ink">{p.historyHeading}</h2>
          {rows === null ? (
            <Spinner className="h-4 w-4" />
          ) : decided.length === 0 ? (
            <p className="py-2 text-xs text-ink-muted">{p.historyNone}</p>
          ) : (
            <div className="thin-scroll overflow-x-auto rounded border border-line">
              <table className="w-full min-w-[900px] text-left text-xs">
                <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className={th}>{p.colBlock}</th>
                    <th className={th}>{p.colRequestedBy}</th>
                    <th className={th}>{p.colReason}</th>
                    <th className={th}>{p.colStatus}</th>
                    <th className={th}>{p.colDecidedBy}</th>
                    <th className={th}>{p.colValidUntil}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {decided.map((r) => (
                    <tr key={r.id}>
                      <td className={`${td} text-ink`}>
                        <span className="font-medium">{r.block_name ?? `#${r.block_id}`}</span>
                        <p className="text-2xs text-ink-muted">{place(r)}</p>
                      </td>
                      <td className={`${td} text-ink-secondary`}>
                        {who(r)}
                        <p className="text-2xs tabular-nums text-ink-muted">{when(r.requested_at)}</p>
                      </td>
                      <td className={`${td} max-w-[320px] whitespace-pre-wrap text-ink-secondary`}>{r.reason}</td>
                      <td className={`${td} text-ink-secondary`}>
                        {p.status[r.status]}
                        {r.decision_note && <p className="text-2xs text-ink-muted">“{r.decision_note}”</p>}
                      </td>
                      <td className={`${td} text-ink-secondary`}>
                        {r.decided_by ?? "—"}
                        <p className="text-2xs tabular-nums text-ink-muted">{when(r.decided_at)}</p>
                      </td>
                      <td className={`${td} whitespace-nowrap tabular-nums text-ink-secondary`}>
                        {when(r.valid_until)}
                        {r.reopened_for && <p className="text-2xs text-ink-muted">{p.forUser(r.reopened_for)}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={approving !== null}
        danger={false}
        title={p.approveTitle(approving?.block_name ?? "")}
        body={p.approveBody(approving ? who(approving) : "")}
        confirmLabel={p.approve}
        cancelLabel={t.common.cancel}
        busy={busy}
        confirmDisabled={!validIso}
        onCancel={() => setApproving(null)}
        onConfirm={() => {
          if (approving && validIso)
            void act(
              () => approveReopenRequest(approving.id, { valid_until: validIso, note: note.trim() || undefined }),
              p.noticeApproved(approving.block_name ?? "")
            );
        }}
      >
        {approving && (
          <p className="mb-2.5 whitespace-pre-wrap rounded bg-surface-sunken p-2.5 text-xs text-ink-secondary">
            <span className="font-medium text-ink">{c.reasonLabel}: </span>
            {approving.reason}
          </p>
        )}
        <ReopenFields reason="" onReason={() => {}} validUntil={validUntil} onValidUntil={setValidUntil} showReason={false} />
        <label className="mt-2.5 block">
          <span className="mb-1 block text-xs font-medium text-ink">{p.noteOptional}</span>
          <input
            className="w-full rounded border border-line bg-surface px-2.5 py-1.5 text-xs text-ink"
            value={note}
            maxLength={2000}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {error && <Banner tone="danger">{error}</Banner>}
      </ConfirmDialog>

      <ConfirmDialog
        open={rejecting !== null}
        title={p.rejectTitle(rejecting?.block_name ?? "")}
        body={p.rejectBody(rejecting ? who(rejecting) : "")}
        confirmLabel={p.reject}
        cancelLabel={t.common.cancel}
        busy={busy}
        confirmDisabled={note.trim().length < 3}
        onCancel={() => setRejecting(null)}
        onConfirm={() => {
          if (rejecting)
            void act(() => rejectReopenRequest(rejecting.id, note.trim()), p.noticeRejected(rejecting.block_name ?? ""));
        }}
      >
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink">{p.rejectNote} *</span>
          <textarea
            className="w-full rounded border border-line bg-surface px-2.5 py-1.5 text-xs text-ink"
            rows={3}
            value={note}
            maxLength={2000}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {error && <Banner tone="danger">{error}</Banner>}
      </ConfirmDialog>
    </AppShell>
  );
}
